import React, { useEffect, useState } from "react";
import { Copy, Check, Archive, ArchiveRestore, Plus } from "lucide-react";
import { toast } from "sonner";
import { mktLinks, mktCreateLink, mktPatchLink, apiError } from "../../../lib/adminApi";
import { Panel, Empty, Field, inputCls, btnPrimary, btnGhost, fmtDate } from "../ui";

const CHANNEL_STYLE = {
  instagram: "bg-pink-500/15 text-pink-300 ring-pink-400/30", tiktok: "bg-cyan-500/15 text-cyan-300 ring-cyan-400/30",
  ads: "bg-amber-500/15 text-amber-300 ring-amber-400/30", influencer: "bg-teal-500/15 text-teal-300 ring-teal-400/30", other: "bg-white/5 text-slate-300 ring-white/10",
};
export const ChannelBadge = ({ channel, label }) => (
  <span className={`inline-flex px-2 py-0.5 rounded-full text-[11px] font-medium ring-1 ${CHANNEL_STYLE[channel] || CHANNEL_STYLE.other}`}>{label || channel}</span>
);

const CopyBtn = ({ text, testid }) => {
  const [ok, setOk] = useState(false);
  const copy = async () => {
    try { await navigator.clipboard.writeText(text); } catch { window.prompt("Copie o link:", text); }
    setOk(true); toast.success("Link copiado"); setTimeout(() => setOk(false), 1500);
  };
  return <button onClick={copy} data-testid={testid} className={`${btnGhost} h-8 px-3`}>{ok ? <Check size={14} className="text-emerald-400" /> : <Copy size={14} />} {ok ? "Copiado" : "Copiar"}</button>;
};

const EMPTY = { name: "", channel: "instagram", destination: "landing", utm_campaign: "", utm_content: "" };

export default function LinksPanel() {
  const [data, setData] = useState(null);
  const [showArchived, setShowArchived] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);

  const load = () => mktLinks({ include_archived: showArchived ? 1 : 0 }).then(setData).catch((e) => toast.error(apiError(e)));
  useEffect(() => { load(); }, [showArchived]); // eslint-disable-line

  const create = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || busy) return;
    setBusy(true);
    try {
      const l = await mktCreateLink(form);
      toast.success(`Link "${l.name}" criado`);
      setForm(EMPTY);
      await load();
    } catch (err) { toast.error(apiError(err)); }
    setBusy(false);
  };

  const toggleArchive = async (l) => {
    try { await mktPatchLink(l.id, { archived: !l.archived }); await load(); } catch (err) { toast.error(apiError(err)); }
  };

  const channels = data?.channels || [];
  const links = data?.links || [];
  const ch = channels.find((c) => c.id === form.channel);

  return (
    <div className="grid lg:grid-cols-[360px_1fr] gap-6">
      <Panel title="Novo link" testid="mkt-new-link">
        <form onSubmit={create} className="space-y-4">
          <Field label="Nome"><input data-testid="mkt-link-name" className={inputCls} placeholder="Ex.: Bio do Instagram" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></Field>
          <Field label="Canal">
            <div className="grid grid-cols-2 gap-1.5">
              {channels.map((c) => (
                <button type="button" key={c.id} data-testid={`mkt-channel-${c.id}`} onClick={() => setForm({ ...form, channel: c.id })}
                  className={`h-9 rounded-lg text-[12.5px] ring-1 transition-colors ${form.channel === c.id ? "bg-[#38BDF8]/25 text-white ring-[#38BDF8]/50" : "text-slate-300 ring-white/10 hover:bg-white/5"}`}>{c.label}</button>
              ))}
            </div>
          </Field>
          <Field label="Destino">
            <div className="flex gap-1.5">
              {[["landing", "Landing page"], ["app", "App (cadastro)"]].map(([v, l]) => (
                <button type="button" key={v} data-testid={`mkt-dest-${v}`} onClick={() => setForm({ ...form, destination: v })}
                  className={`flex-1 h-9 rounded-lg text-[12.5px] ring-1 ${form.destination === v ? "bg-[#38BDF8]/25 text-white ring-[#38BDF8]/50" : "text-slate-300 ring-white/10 hover:bg-white/5"}`}>{l}</button>
              ))}
            </div>
          </Field>
          <Field label="Campanha (utm_campaign)"><input data-testid="mkt-link-campaign" className={inputCls} placeholder={form.name ? form.name.toLowerCase().replace(/\s+/g, "-") : "ex.: lancamento-junho"} value={form.utm_campaign} onChange={(e) => setForm({ ...form, utm_campaign: e.target.value })} /></Field>
          <Field label="Conteúdo / criativo (opcional)"><input data-testid="mkt-link-content" className={inputCls} placeholder="ex.: reels-01, stories" value={form.utm_content} onChange={(e) => setForm({ ...form, utm_content: e.target.value })} /></Field>
          {ch && <p className="text-[11.5px] text-slate-500">utm_source=<b className="text-slate-300">{ch.utm_source}</b> · utm_medium=<b className="text-slate-300">{ch.utm_medium}</b> · utm_id gerado automaticamente</p>}
          <button type="submit" disabled={busy || !form.name.trim()} data-testid="mkt-link-create" className={`${btnPrimary} w-full`}><Plus size={15} /> Gerar link</button>
        </form>
      </Panel>

      <Panel title={`Links (${links.length})`} testid="mkt-links-list"
        action={<label className="flex items-center gap-2 text-[12px] text-slate-400 cursor-pointer"><input type="checkbox" checked={showArchived} onChange={(e) => setShowArchived(e.target.checked)} data-testid="mkt-show-archived" /> mostrar arquivados</label>}>
        {!links.length ? <Empty text="Nenhum link ainda. Crie o primeiro ao lado." /> : (
          <div className="space-y-3">
            {links.map((l) => (
              <div key={l.id} data-testid={`mkt-link-${l.id}`} className={`rounded-xl ring-1 ring-white/[0.06] p-4 ${l.archived ? "opacity-60" : "bg-white/[0.02]"}`}>
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-[14px] font-semibold text-white">{l.name}</span>
                  <ChannelBadge channel={l.channel} label={l.channel_label} />
                  <span className="text-[11px] text-slate-500">{l.destination === "app" ? "→ app" : "→ landing"} · {fmtDate(l.created_at)} · <code className="text-slate-400">{l.id}</code></span>
                  {l.archived && <span className="text-[11px] text-amber-300">arquivado</span>}
                  <div className="ml-auto flex items-center gap-1.5">
                    <CopyBtn text={l.url} testid={`mkt-copy-${l.id}`} />
                    <button onClick={() => toggleArchive(l)} data-testid={`mkt-archive-${l.id}`} className={`${btnGhost} h-8 px-3`} title={l.archived ? "Restaurar" : "Arquivar"}>{l.archived ? <ArchiveRestore size={14} /> : <Archive size={14} />}</button>
                  </div>
                </div>
                <div className="mt-2 font-mono-jb text-[12px] text-[#7DD3FC] break-all select-all" data-testid={`mkt-url-${l.id}`}>{l.url}</div>
              </div>
            ))}
          </div>
        )}
      </Panel>
    </div>
  );
}
